import { ChangeEvent, useEffect, useState } from "react";
import StatusBadge from "./StatusBadge";
import StageOutOfScopeMessage from "./StageOutOfScopeMessage";
import IntegrationCredentialSections, {
  CredentialFormFields,
} from "./IntegrationCredentialSections";
import {
  AuthTypeId,
  ConnectionTestResult,
  DEFAULT_AUTH_TYPE,
  HttpMethodId,
  getPasSamplePayload,
  normalizePasFormat,
  PAS_FORMAT_OPTIONS,
  STAGE_DESCRIPTIONS,
  STAGE_META,
  ServiceProvider,
  StageConfig,
} from "../../../utils/integrationConfigData";
import {
  AuthTypeApi,
  ConfigurationTypeApi,
  mapAuthTypeIdToApiId,
  mapIntegrationToStageConfig,
} from "../types/providerIntegrationtypes";
import { isTestSuccess } from "../../../constants/integrationKeys";
import {
  formatTestResultMessage,
  saveStageConfig,
} from "../../../utils/integrationApiHelpers";
import { useAuditUserId } from "../../../hooks/useAuditUserId";
import { useStageConfigLoader } from "../../../utils/useStageConfigLoader";
import { KeyValueItem } from "../../../utils/keyValueItems";
import { ProviderIntegrationApi } from "../../../service/Api/ProviderIntegrationApi";

interface StagePasFormProps {
  provider: ServiceProvider;
  stage?: StageConfig;
  showInfoBanner?: boolean;
  authTypes?: AuthTypeApi[];
  onProvidersChanged?: () => void;
}

type FormState = CredentialFormFields & {
  requestFormat: string;
  responseFormat: string;
};

const buildFormState = (stage?: StageConfig): FormState => ({
  endpoint: stage?.endpoint || "",
  authType: stage?.authType || DEFAULT_AUTH_TYPE,
  httpMethod: stage?.httpMethod || "GET",
  requestUrls: stage?.requestUrls || [],
  headers: stage?.headers || [],
  credentialHeaders: stage?.credentialHeaders || [],
  queryParams: stage?.queryParams || [],
  requestBody: stage?.requestBody || [],
  bearerToken: stage?.bearerToken || "",
  username: stage?.username || "",
  password: stage?.password || "",
  headerName: stage?.headerName || "",
  apiKey: stage?.apiKey || "",
  requestFormat: normalizePasFormat(stage?.requestFormat),
  responseFormat: normalizePasFormat(stage?.responseFormat),
});

const StagePasForm = ({
  provider,
  stage,
  showInfoBanner = true,
  authTypes = [],
  onProvidersChanged,
}: StagePasFormProps) => {
  const meta = STAGE_META.pas;
  const description = stage?.description || STAGE_DESCRIPTIONS.pas;
  const auditUserId = useAuditUserId();

  const [configurationTypes, setConfigurationTypes] = useState<
    ConfigurationTypeApi[]
  >([]);
  const [selectedConfigurationTypeId, setSelectedConfigurationTypeId] =
    useState<number | null>(null);

  const { loadedStage, setLoadedStage, loadError, isLoading, reloadForAuthType } =
    useStageConfigLoader(
      provider.id,
      "pas",
      authTypes,
      stage,
      selectedConfigurationTypeId,
    );

  const [form, setForm] = useState<FormState>(() => buildFormState(stage));
  const [previewFormat, setPreviewFormat] = useState(
    () => buildFormState(stage).requestFormat,
  );
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(
    stage?.testResult || null,
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    ProviderIntegrationApi.getConfigurationTypes("PAS")
      .then((types) => {
        if (cancelled) return;
        setConfigurationTypes(types);
        if (types.length && selectedConfigurationTypeId == null) {
          setSelectedConfigurationTypeId(types[0].id);
        }
      })
      .catch(() => {
        if (!cancelled) setConfigurationTypes([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const next = buildFormState(loadedStage);
    setForm((prev) => ({
      ...next,
      authType: loadedStage?.authType || prev.authType,
    }));
    setPreviewFormat(next.requestFormat);
    setTestResult(loadedStage?.testResult || null);
  }, [loadedStage]);

  useEffect(() => {
    if (loadError) {
      setTestResult({ message: loadError, status: "error" });
    }
  }, [loadError]);

  const handleConfigurationTypeChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const nextId = Number(event.target.value);
    setSelectedConfigurationTypeId(Number.isFinite(nextId) ? nextId : null);
    setTestResult(null);
  };

  const handleChange =
    (field: keyof FormState) =>
      (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const value = event.target.value;
        setForm((prev) => ({
          ...prev,
          [field]:
            field === "authType"
              ? (value as AuthTypeId)
              : field === "httpMethod"
                ? (value as HttpMethodId)
                : value,
        }));
        setTestResult(null);

        if (field === "requestFormat" || field === "responseFormat") {
          setPreviewFormat(normalizePasFormat(value));
        }

        if (field === "authType") {
          const authTypeId = mapAuthTypeIdToApiId(value as AuthTypeId, authTypes);
          if (authTypeId) reloadForAuthType(authTypeId);
        }
      };

  const handleListChange = (
    field:
      | "requestUrls"
      | "headers"
      | "credentialHeaders"
      | "queryParams"
      | "requestBody",
    items: KeyValueItem[],
  ) => {
    setForm((prev) => ({ ...prev, [field]: items }));
    setTestResult(null);
  };

  const getValidationError = (): string | null => {
    if (!selectedConfigurationTypeId) {
      return "Failed · Missing PAS endpoint type · 0ms";
    }
    if (!form.endpoint.trim()) {
      return "Failed · Missing endpoint · 0ms";
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = getValidationError();
    if (validationError) {
      setTestResult({ message: validationError, status: "error" });
      return;
    }

    const providerId = Number(provider.id);
    if (!providerId || !selectedConfigurationTypeId) return;

    setIsSaving(true);
    try {
      const result = await saveStageConfig(
        providerId,
        "pas",
        {
          ...form,
          // Preview-only; API does not persist SamplePayload.
          samplePayload: undefined,
        },
        authTypes,
        auditUserId,
        selectedConfigurationTypeId,
      );
      setTestResult({
        message: formatTestResultMessage(result),
        status: isTestSuccess(result.testResult?.testStatus)
          ? "success"
          : "error",
      });
      setLoadedStage({
        ...mapIntegrationToStageConfig(result),
        configurationId: result.id,
      });
      onProvidersChanged?.();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed · Save failed · 0ms";
      setTestResult({
        message: message.startsWith("Failed")
          ? message
          : `Failed · ${message} · 0ms`,
        status: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const fieldId = (name: string) => `pas-${name}`;
  const displayStatus = loadedStage?.status || stage?.status || "notSet";
  const isNotSet = displayStatus === "notSet";
  const previewPayload = getPasSamplePayload(previewFormat);

  return (
    <div className="ic-stage-form-panel">
      {showInfoBanner && (
        <div className="ic-info-banner" role="note">
          <i className="fh_info_fill" aria-hidden="true"></i>
          <span>
            Configure this vendor authorization endpoint, then Test &amp; Save
            Configuration.
          </span>
        </div>
      )}

      <div className="ic-stage-form-shell">
        <div className="ic-stage-form-content">
          <header className="ic-stage-form-header">
            <div className="ic-stage-form-title-row">
              <span
                className="ic-stats-icon"
              >
                <i className={meta.icon}></i>
              </span>
              <div className="ic-stage-form-heading-text">
                <div className="ic-stage-form-title-line">
                  <h2 className="ic-stage-form-title">{meta.label}</h2>
                  <StatusBadge status={displayStatus} />
                </div>
                <p className="ic-stage-form-desc mb-0">{description}</p>
              </div>
            </div>
          </header>

          {isLoading && (
            <div className="ic-inline-message" role="status">
              Loading PAS configuration...
            </div>
          )}

          {isNotSet && !isLoading && (
            <StageOutOfScopeMessage stageLabel={meta.label} />
          )}

          <section className="ic-stage-form-section">
            <h3 className="ic-stage-form-section-title">PAS endpoint</h3>
            <div className="ic-stage-form-field">
              <label
                className="ic-stage-form-label"
                htmlFor={fieldId("endpoint-type")}
              >
                PAS endpoint
              </label>
              <select
                id={fieldId("endpoint-type")}
                className="form-select form-select-sm ic-stage-form-input ic-stage-form-select"
                value={selectedConfigurationTypeId ?? ""}
                onChange={handleConfigurationTypeChange}
                disabled={!configurationTypes.length}
              >
                {configurationTypes.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.displayName}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <IntegrationCredentialSections
            form={form}
            fieldId={fieldId}
            onFieldChange={handleChange}
            onListChange={handleListChange}
            connectionActive={
              loadedStage?.configurationId != null
                ? !Boolean(loadedStage.isDeleted)
                : undefined
            }
          />

          <section className="ic-stage-form-section">
            <h3 className="ic-stage-form-section-title">PAS formats</h3>
            <div className="ic-stage-form-grid ic-stage-form-grid-stack">
              <div className="ic-stage-form-field">
                <label
                  className="ic-stage-form-label"
                  htmlFor={fieldId("request-format")}
                >
                  Request format
                </label>
                <select
                  id={fieldId("request-format")}
                  className="form-select form-select-sm ic-stage-form-input ic-stage-form-select"
                  value={form.requestFormat}
                  onChange={handleChange("requestFormat")}
                >
                  {PAS_FORMAT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ic-stage-form-field">
                <label
                  className="ic-stage-form-label"
                  htmlFor={fieldId("response-format")}
                >
                  Response format
                </label>
                <select
                  id={fieldId("response-format")}
                  className="form-select form-select-sm ic-stage-form-input ic-stage-form-select"
                  value={form.responseFormat}
                  onChange={handleChange("responseFormat")}
                >
                  {PAS_FORMAT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <div className="ic-pas-preview">
            <pre className="ic-pas-preview-code">
              <code>{previewPayload}</code>
            </pre>
          </div>

          <footer className="ic-stage-form-footer">
            <button
              type="button"
              className="btn btn-primary btn-sm ic-test-connection-btn"
              onClick={handleSave}
              disabled={isSaving || isLoading || !selectedConfigurationTypeId}
            >
              {isSaving ? (
                <>
                  <span className="ic-test-spinner" aria-hidden="true" />
                  Testing &amp; saving...
                </>
              ) : (
                "Test & Save Configuration"
              )}
            </button>

            {testResult ? (
              <div
                className={`ic-test-result ic-test-result-${testResult.status}`}
                role="status"
              >
                <i
                  className={
                    testResult.status === "error"
                      ? "fh_error_line"
                      : "fh_check_circle_line"
                  }
                  aria-hidden="true"
                ></i>
                <span>{testResult.message}</span>
              </div>
            ) : (
              <span className="ic-test-pending" role="status">
                Not tested yet
              </span>
            )}
          </footer>
        </div>
      </div>
    </div>
  );
};

export default StagePasForm;
